import { useEffect, useId, useMemo, useRef, useState } from "react";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  startStaffReportTemplateExtract,
  getStaffReportTemplateExtract,
  listStaffReportTemplates,
  createStaffReportTemplate,
  updateStaffReportTemplate,
  discardStaffReportTemplate,
  reviseStaffReport,
  saveStaffReportDraft,
  updateStaffReportDraft,
  discardStaffReportDraft,
  getStaffReportDraft,
  getDraftRevisions,
  type StaffReportDraftRevisionData,
  createResearchSet,
  prepareResearchSet,
  getResearchSet,
  continueResearchSet,
  startStaffReportRun,
  getStaffReportRun,
  RESEARCH_TERMINAL_STATUSES,
  RESEARCH_MAX_FILES,
  type StaffReportResearchSet,
  type StaffReportResearchDocument,
  type StaffReportRun,
  type StaffReportStage,
} from "@/api/staff_reports";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useStaffReports } from "@/context/StaffReportsContext";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { moduleWorkspaceNarrowContentClass, moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { StaffReportSources, researchDocumentLabel, type ReusedReportSource } from "@/components/staff_reports/StaffReportSources";
import { StaffReportDraftsList } from "@/components/staff_reports/StaffReportDraftsList";
import { StaffReportStageBadge } from "@/components/staff_reports/StaffReportStageBadge";
import RichTextEditor from "@/components/editor/RichTextEditor";
import { ArrowLeft, ArrowRight, Check, Loader2, CheckCircle2, Sparkles, Copy as CopyIcon, Printer, Trash2, Undo2 } from "lucide-react";
import { DismissibleProgressDialog } from "@/components/staff_reports/DismissibleProgressDialog";
import { ResearchQuestionsPanel } from "@/components/staff_reports/ResearchQuestionsPanel";
import {
  TemplateProposalEditor,
  toProposalSections,
  toTemplateSections,
  type ProposalSection,
  type TemplateSection,
} from "@/components/staff_reports/TemplateProposalEditor";
import { openStaffReportPrintPreview } from "@/components/staff_reports/staffReportPrint";

const TEMPLATE_FILE_EXTENSIONS = [".pdf", ".docx", ".txt", ".md"];
const EXTRACT_POLL_MS = 1200;
// 1200ms * 2175 covers ExpireExtractRunJob::WAIT (2610s).
const EXTRACT_POLL_MAX = 2175;
const EXTRACT_TERMINAL = new Set(["done", "failed"]);
const RESEARCH_POLL_MS = 1200;
// Stop before the 20 minute stale-claim requeue leaves the dialog spinning.
const RESEARCH_POLL_MAX_MS = 10 * 60 * 1000;
const RESEARCH_POLL_FAILURE_LIMIT = 5;
const RESEARCH_BUSY_STATUSES = ["queued", "parsing", "assembling"];
const GENERATE_POLL_MS = 1200;
// Brief, extracts, and compose each use a 240s Luna timeout. Twenty minutes
// covers one successful pass. An orphaned run has no reaper.
const GENERATE_POLL_MAX_MS = 20 * 60 * 1000;
const GENERATE_POLL_FAILURE_LIMIT = 5;

function isTemplateFile(file: File) {
  const name = file.name.toLowerCase();
  return TEMPLATE_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function isResearchTerminal(status: string | null | undefined) {
  return RESEARCH_TERMINAL_STATUSES.includes(status as (typeof RESEARCH_TERMINAL_STATUSES)[number]);
}

function generateStageCopy(stage: string) {
  switch (stage) {
    case "writing_brief":
      return "Writing the brief";
    case "composing":
      return "Composing the draft";
    default: {
      const extracting = /^extracting(?: (\d+)\/(\d+))?$/.exec(stage);
      if (extracting?.[1] && extracting[2]) {
        return `Reading research ${extracting[1]} of ${extracting[2]}`;
      }
      if (extracting) return "Reading research";
      return stage;
    }
  }
}

export function StaffReportsAssistant() {
  const fileInputId = useId();
  const factsInputId = useId();
  const templateSectionId = useId();
  const detailsSectionId = useId();
  const preparationPanelRef = useRef<HTMLDivElement>(null);
  const detailsStepRef = useRef<HTMLButtonElement>(null);
  const researchMunicipalityIdRef = useRef<number | null>(null);

  const [workspaceTab, setWorkspaceTab] = useState<"preparation" | "report">("preparation");
  const [preparationStep, setPreparationStep] = useState<"template" | "details">("template");
  const [showUploadTemplate, setShowUploadTemplate] = useState(false);

  useEffect(() => {
    preparationPanelRef.current?.scrollIntoView({ block: "start" });
  }, [preparationStep]);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [template, setTemplate] = useState<{ title: string; sections: TemplateSection[]; style?: any } | null>(null);
  const [facts, setFacts] = useState("");
  const [templates, setTemplates] = useState<Array<{ id: number; name: string; template_json: any }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");
  const [templateName, setTemplateName] = useState("");
  const [templateLabel, setTemplateLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultMd, setResultMd] = useState<string | null>(null);
  const [outline, setOutline] = useState<{ title: string; bullets: string[] }[] | null>(null);
  const { municipality } = useMunicipality();
  const previousMunicipalityId = useRef(municipality?.id);
  const [view, setView] = useState<"edit" | "preview">("preview");
  const [showToolbar, setShowToolbar] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const draftRef = useRef<HTMLDivElement | null>(null);
  const scrollToDraft = () => {
    try { draftRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch {}
  };
  const [reviseText, setReviseText] = useState("");
  const [revising, setRevising] = useState(false);
  const reviseAttemptRef = useRef(0);
  const resultMdRef = useRef<string | null>(null);
  const lastSavedDraftIdRef = useRef<number | null>(null);
  const { workspaceView, setWorkspaceView, selectedDraft, drafts, setDrafts, setSelectedDraft, nextDraftSelectionVersion, getDraftSelectionVersion, beforeLeaveReportRef } = useStaffReports();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lastSavedDraftId, setLastSavedDraftId] = useState<number | null>(null);
  const [preparationSaved, setPreparationSaved] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const savedPreparationRef = useRef("");
  const failedPreparationRef = useRef("");
  const saveInFlightRef = useRef<Promise<number> | null>(null);
  const workspaceSaverRef = useRef<(stageSources?: boolean) => Promise<number>>(null!);
  resultMdRef.current = resultMd;
  lastSavedDraftIdRef.current = lastSavedDraftId;
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState<{ name: string; markdown: string; facts: string; staff_report_template_id?: number; workspaceState: string } | null>(null);
  // Research set (step two)
  const [researchFiles, setResearchFiles] = useState<File[]>([]);
  const [reusedSources, setReusedSources] = useState<ReusedReportSource[]>([]);
  const [loadedResearchSetId, setLoadedResearchSetId] = useState<number | null>(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const [sourceRetry, setSourceRetry] = useState(0);
  const [researchSet, setResearchSet] = useState<StaffReportResearchSet | null>(null);
  const [researchDocs, setResearchDocs] = useState<StaffReportResearchDocument[]>([]);
  const [researchAnswerTexts, setResearchAnswerTexts] = useState<Record<number, string>>({});
  const [researchSubmitting, setResearchSubmitting] = useState(false);
  const [researchContinuePending, setResearchContinuePending] = useState(false);
  const [researchContinueError, setResearchContinueError] = useState<string | null>(null);
  const [researchStalled, setResearchStalled] = useState(false);
  const [researchProgressBody, setResearchProgressBody] = useState("This may take a moment.");
  const [generateBusy, setGenerateBusy] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<StaffReportRun["status"] | null>(null);
  const [generateProgressOpen, setGenerateProgressOpen] = useState(false);
  const [generateStage, setGenerateStage] = useState("writing_brief");
  const pollRef = useRef<number | null>(null);
  const generatePollRef = useRef<number | null>(null);
  const generateGenerationRef = useRef(0);
  const generateStartedSetIdRef = useRef<number | null>(null);
  const applyGenerateRunRef = useRef<(run: StaffReportRun) => boolean>(() => false);

  // Revision history state
  type Revision = StaffReportDraftRevisionData;
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [revisionsReady, setRevisionsReady] = useState(false);
  const [currentRevisionNumber, setCurrentRevisionNumber] = useState<number | null>(null);

  // Richer progress dialog state
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressTitle, setProgressTitle] = useState<string>("Processing…");
  const [progressBody, setProgressBody] = useState<string>("This may take a moment.");
  const [progressMetrics, setProgressMetrics] = useState<{ processed: number; total: number }>({ processed: 0, total: 0 });
  const [displayProgress, setDisplayProgress] = useState<{ processed: number; total: number }>({ processed: 0, total: 0 });
  const progressAnimationRef = useRef<number | null>(null);
  const displayProgressRef = useRef<{ processed: number; total: number }>({ processed: 0, total: 0 });
  const extractPollRef = useRef<number | null>(null);
  const extractGenerationRef = useRef(0);
  const [extractProgressOpen, setExtractProgressOpen] = useState(false);
  const [extractRunId, setExtractRunId] = useState<number | null>(null);
  const [proposalRows, setProposalRows] = useState<ProposalSection[]>([]);

  // Load selected draft into editor
  useEffect(() => {
    reviseAttemptRef.current += 1;
    setRevising(false);
    if (!selectedDraft) return;
    generateGenerationRef.current += 1;
    stopResearchPoll();
    stopGeneratePoll();
    generateStartedSetIdRef.current = null;
    setGenerateBusy(false);
    setGenerationStatus(null);
    setGenerateProgressOpen(false);
    setProgressOpen(false);
    setSaving(false);
    failedPreparationRef.current = "";
    setResearchContinuePending(false);
    abandonExtractPoll();
    setExtractProgressOpen(false);
    setSelectedFiles([]);
    setResearchSet(null);
    setResearchDocs([]);
    setResearchFiles([]);
    setReusedSources([]);
    setSourcesLoading(false);
    setSourcesError(null);
    setLoadedResearchSetId(selectedDraft.staff_report_research_set_id ?? null);
    setResultMd(selectedDraft.markdown || null);
    setTemplateName(selectedDraft.name || "");
    setSelectedTemplateId(selectedDraft.staff_report_template_id ?? "");
    setFacts(selectedDraft.facts || "");
    const workspace = selectedDraft.workspace_state;
    const restoredTemplate = workspace?.template ? { ...workspace.template, title: workspace.template.title || "" } : null;
    setTemplate(restoredTemplate);
    setProposalRows(toProposalSections(workspace?.template?.sections ?? []));
    setTemplateLabel(workspace?.template?.title ?? "");
    setResearchAnswerTexts(workspace?.answers ?? {});
    setExtractRunId(workspace?.extract_run_id ?? null);
    setWorkspaceTab(selectedDraft.markdown ? "report" : "preparation");
    setPreparationStep(selectedDraft.staff_report_template_id || workspace?.template || selectedDraft.staff_report_research_set_id ? "details" : "template");
    savedPreparationRef.current = "";
    setPreparationSaved(false);
    setView("preview");
    setShowToolbar(false);

    setLastSavedDraftId(selectedDraft.id);
    setLastSavedSnapshot({
      name: selectedDraft.name || "",
      markdown: selectedDraft.markdown || "",
      facts: selectedDraft.facts || "",
      staff_report_template_id: selectedDraft.staff_report_template_id ?? undefined,
      workspaceState: JSON.stringify({ template: restoredTemplate, answers: workspace?.answers ?? {}, extract_run_id: workspace?.extract_run_id ?? null }),
    });
    setRevisions([]);
    setRevisionsReady(false);
    setCurrentRevisionNumber(null);
    setError(null);
    if (!selectedDraft.markdown && selectedDraft.staff_report_run_id) {
      generateStartedSetIdRef.current = selectedDraft.staff_report_research_set_id ?? null;
      setGenerateBusy(true);
      startGeneratePoll(selectedDraft.staff_report_run_id, selectedDraft.municipality_id);
    }

    let cancelled = false;
    getDraftRevisions(selectedDraft.id)
      .then((list) => {
        if (cancelled) return;
        setRevisions(list);
        setRevisionsReady(true);
        setCurrentRevisionNumber(list.length > 0 ? list[0].revision_number : null);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setRevisions([]);
          setRevisionsReady(false);
          setCurrentRevisionNumber(null);
          if (e instanceof Error) setError(e.message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedDraft]);

  useEffect(() => {
    if (!loadedResearchSetId || !municipality?.id) {
      setSourcesLoading(false);
      return;
    }
    let cancelled = false;
    setSourcesLoading(true);
    setSourcesError(null);
    getResearchSet(loadedResearchSetId, municipality.id).then((set) => {
      if (cancelled) return;
      setResearchSet(set);
      setResearchDocs(set.documents);
      if (!isResearchTerminal(set.status) && set.status !== "collecting") startResearchPoll(set.id, set.municipality_id);
    }).catch((e) => {
      if (!cancelled) setSourcesError(e instanceof Error ? e.message : "Could not load supporting files.");
    }).finally(() => { if (!cancelled) setSourcesLoading(false); });
    return () => { cancelled = true; };
  }, [loadedResearchSetId, municipality?.id, selectedDraft, sourceRetry]);

  useEffect(() => {
    if (!extractRunId || template || selectedDraft?.workspace_state?.extract_run_id !== extractRunId) return;
    let cancelled = false;
    let failures = 0;
    const poll = async () => {
      try {
        const run = await getStaffReportTemplateExtract(extractRunId);
        if (cancelled) return;
        failures = 0;
        if (!EXTRACT_TERMINAL.has(run.status)) return;
        window.clearInterval(interval);
        if (run.proposal?.sections?.length) applyProposal(run.proposal);
        else setError(run.error_message || "Template extraction failed.");
      } catch (error) {
        if (cancelled || ++failures < 5) return;
        window.clearInterval(interval);
        setError(error instanceof Error ? error.message : "Could not load your reference reports.");
      }
    };
    const interval = window.setInterval(poll, EXTRACT_POLL_MS);
    void poll();
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [extractRunId, selectedDraft?.id, template]);

  useEffect(() => {
    displayProgressRef.current = displayProgress;
  }, [displayProgress]);

  useEffect(() => {
    if (progressAnimationRef.current) {
      window.clearInterval(progressAnimationRef.current);
      progressAnimationRef.current = null;
    }

    const current = displayProgressRef.current;

    if (progressMetrics.total !== current.total || progressMetrics.processed < current.processed) {
      setDisplayProgress((prev) => ({
        processed: Math.min(prev.processed, progressMetrics.processed),
        total: progressMetrics.total,
      }));
    }

    if (progressMetrics.processed <= current.processed) {
      setDisplayProgress({
        processed: progressMetrics.processed,
        total: progressMetrics.total,
      });
      return;
    }

    progressAnimationRef.current = window.setInterval(() => {
      setDisplayProgress((prev) => {
        if (prev.processed >= progressMetrics.processed) {
          if (progressAnimationRef.current) {
            window.clearInterval(progressAnimationRef.current);
            progressAnimationRef.current = null;
          }
          return {
            processed: progressMetrics.processed,
            total: progressMetrics.total,
          };
        }
        return {
          processed: Math.min(progressMetrics.processed, prev.processed + 1),
          total: progressMetrics.total,
        };
      });
    }, 80);

    return () => {
      if (progressAnimationRef.current) {
        window.clearInterval(progressAnimationRef.current);
        progressAnimationRef.current = null;
      }
    };
  }, [progressMetrics.processed, progressMetrics.total]);

  const researchReady = useMemo(() => {
    const status = researchSet?.status;
    return status === "done" || status === "partial";
  }, [researchSet?.status]);

  const researchBusy = !researchStalled && (RESEARCH_BUSY_STATUSES.includes(researchSet?.status || "") || researchSubmitting);
  const researchInputsLocked = !!researchSet && !["collecting", "failed"].includes(researchSet.status) && !researchStalled;
  const reportStage: StaffReportStage = resultMd ? "ready_to_review"
    : generateBusy ? "writing"
    : generationStatus === "failed" || researchSet?.status === "failed" ? "needs_attention"
    : researchSet?.status === "needs_input" ? "questions"
    : researchReady ? "ready_to_write"
    : RESEARCH_BUSY_STATUSES.includes(researchSet?.status || "") ? "reading_files"
    : researchSet ? "preparation"
    : selectedDraft?.stage ?? "preparation";
  const factsProcessing = useMemo(
    () => researchDocs.some((d) => !["done", "failed", "skipped"].includes(d.status)),
    [researchDocs],
  );
  const showDocumentProgress = researchDocs.length > 0 && (factsProcessing || researchBusy);

  // Step validation
  const canProceedFromStep1 = useMemo(() => {
    return !!template || selectedTemplateId !== "";
  }, [template, selectedTemplateId]);

  const canProceedFromStep2 = useMemo(() => {
    if (researchBusy || researchContinuePending) return false;
    if (researchSet?.status === "failed") return false;
    if (researchSet?.status === "needs_input") return false;
    if (researchReady) return false;
    return researchFiles.length + reusedSources.length > 0 || researchSet?.status === "collecting";
  }, [researchBusy, researchContinuePending, researchFiles.length, reusedSources.length, researchReady, researchSet?.status]);

  const currentStaffReportTemplateId =
    typeof selectedTemplateId === "number" ? selectedTemplateId : undefined;

  const workspaceState = { template, answers: researchAnswerTexts, extract_run_id: extractRunId };
  const isDirty = !lastSavedSnapshot
    || lastSavedSnapshot.name !== (templateName || "")
    || lastSavedSnapshot.markdown !== (resultMd || "")
    || lastSavedSnapshot.facts !== (facts || "")
    || lastSavedSnapshot.staff_report_template_id !== currentStaffReportTemplateId
    || lastSavedSnapshot.workspaceState !== JSON.stringify(workspaceState);

  const persistRevision = async (
    instruction: string,
    markdown: string,
    draftId = lastSavedDraftId,
  ): Promise<Revision | null> => {
    if (!municipality?.id) {
      throw new Error("Please select a municipality.");
    }
    const selectionVersion = getDraftSelectionVersion();
    const name = templateName || "Untitled Draft";
    const templateId = currentStaffReportTemplateId;
    const saved = draftId
      ? await updateStaffReportDraft({
        id: draftId,
        name,
        markdown,
        staffReportTemplateId: templateId,
        staffReportResearchSetId: researchSet?.id ?? loadedResearchSetId,
        facts,
        instruction,
        workspaceState,
      })
      : await saveStaffReportDraft({
        name,
        markdown,
        municipalityId: municipality.id,
        staffReportTemplateId: templateId,
        staffReportResearchSetId: researchSet?.id ?? loadedResearchSetId,
        facts,
        instruction,
        workspaceState,
      });
    const rememberDraft = () => {
      setDrafts((prev) => {
        const without = prev.filter((d) => d.id !== saved.id);
        return [{
          id: saved.id,
          name: saved.name,
          updated_at: saved.updated_at,
          discarded_at: saved.discarded_at ?? null,
          staff_report_template_id: templateId,
          staff_report_research_set_id: researchSet?.id ?? loadedResearchSetId,
        }, ...without];
      });
    };
    if (getDraftSelectionVersion() !== selectionVersion) {
      rememberDraft();
      return null;
    }
    if (!saved.revision) {
      throw new Error("Save failed");
    }
    setLastSavedDraftId(saved.id);
    lastSavedDraftIdRef.current = saved.id;
    setTemplateName(name);
    setLastSavedSnapshot({
      name,
      markdown,
      facts: facts || "",
      staff_report_template_id: templateId,
      workspaceState: JSON.stringify(workspaceState),
    });
    rememberDraft();
    return saved.revision;
  };

  const preparationSignature = JSON.stringify({ name: templateName, facts, selectedTemplateId, template, researchAnswerTexts, extractRunId, researchSetId: loadedResearchSetId });
  workspaceSaverRef.current = (stageSources = true) => {
    if (saveInFlightRef.current) return saveInFlightRef.current;
    const save = async () => {
      if (!municipality?.id) throw new Error("Please select a municipality.");
      const signature = preparationSignature;
      const selectionVersion = getDraftSelectionVersion();
      const current = () => getDraftSelectionVersion() === selectionVersion;
      setSaving(true);
      setPreparationSaved(false);
      try {
        let id = lastSavedDraftIdRef.current;
        if (resultMd && isDirty) {
          const revision = await persistRevision("Manual edit", resultMd, id);
          if (revision && current()) {
            setRevisions((prev) => [revision, ...prev]);
            setRevisionsReady(true);
            setCurrentRevisionNumber(revision.revision_number);
          }
          id = lastSavedDraftIdRef.current;
        } else if (!id || (!resultMd && savedPreparationRef.current !== signature)) {
          const values = {
            name: templateName.trim() || "New report",
            facts,
            staffReportTemplateId: currentStaffReportTemplateId ?? null,
            staffReportResearchSetId: researchSet?.id ?? loadedResearchSetId,
            workspaceState,
          };
          const saved = id
            ? await updateStaffReportDraft({ id, ...values })
            : await saveStaffReportDraft({ ...values, municipalityId: municipality.id });
          id = saved.id;
          setDrafts((prev) => [saved, ...prev.filter((draft) => draft.id !== saved.id)]);
          if (current()) {
            lastSavedDraftIdRef.current = id;
            setLastSavedDraftId(id);
            setLastSavedSnapshot((prev) => ({ name: values.name, markdown: resultMd || prev?.markdown || "", facts, staff_report_template_id: currentStaffReportTemplateId, workspaceState: JSON.stringify(workspaceState) }));
          }
        }
        if (!id) throw new Error("Could not save report. Try again.");
        if (stageSources && !researchSet && researchFiles.length + reusedSources.length > 0) {
          const set = await createResearchSet({ municipalityId: municipality.id, draftId: id, deferProcessing: true, description: facts,
            templateHeadings: (template?.sections || []).map((section) => section.heading), files: researchFiles, reusedDocumentIds: reusedSources.map((source) => source.document.id) });
          setDrafts((prev) => prev.map((draft) => draft.id === id ? { ...draft, staff_report_research_set_id: set.id } : draft));
          if (current()) {
            setResearchSet(set);
            setResearchDocs(set.documents);
            setLoadedResearchSetId(set.id);
          }
        }
        if (current()) {
          savedPreparationRef.current = signature;
          failedPreparationRef.current = "";
          setPreparationSaved(true);
          setError(null);
        }
        return id;
      } catch (error) {
        failedPreparationRef.current = signature;
        if (current()) setError(error instanceof Error ? error.message : "Could not save report. Your work is still here.");
        throw error;
      } finally {
        if (current()) setSaving(false);
      }
    };
    const pending = save().finally(() => { saveInFlightRef.current = null; });
    saveInFlightRef.current = pending;
    return pending;
  };

  if (beforeLeaveReportRef) beforeLeaveReportRef.current = async (remember = false) => {
    if (researchSubmitting || revising) return false;
    if (!lastSavedDraftId && !templateName && !facts && !template && !researchFiles.length && !reusedSources.length) return true;
    setLeaving(true);
    try {
      await saveInFlightRef.current;
      const id = await workspaceSaverRef.current();
      if (remember) setSelectedDraft(await getStaffReportDraft(id));
      return true;
    } catch { return false; }
    finally { setLeaving(false); }
  };

  useEffect(() => () => { if (beforeLeaveReportRef) beforeLeaveReportRef.current = null; }, [beforeLeaveReportRef]);

  useEffect(() => {
    if (resultMd || sourcesLoading || saving || !municipality?.id) return;
    if (failedPreparationRef.current === preparationSignature) return;
    if (!lastSavedDraftId && !templateName && !facts && !template && !researchFiles.length && !reusedSources.length) return;
    if (savedPreparationRef.current === preparationSignature) return;
    const timer = window.setTimeout(() => { void workspaceSaverRef.current(false).catch(() => {}); }, 1000);
    return () => window.clearTimeout(timer);
  }, [preparationSignature, researchFiles, reusedSources, lastSavedDraftId, sourcesLoading, saving, resultMd, municipality?.id, researchSet]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (workspaceView === "history") return;
      if (saving || !researchSet && researchFiles.length + reusedSources.length > 0 || (resultMd ? isDirty : savedPreparationRef.current !== preparationSignature)) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [workspaceView, saving, isDirty, resultMd, preparationSignature, researchSet, researchFiles.length, reusedSources.length]);

  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      if (!municipality?.id) {
        setTemplates([]);
        return;
      }
      try {
        const list = await listStaffReportTemplates({ municipalityId: municipality.id });
        if (!cancelled) setTemplates(list);
      } catch {
        // silent fail on list
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [municipality?.id]);

  const stopExtractPoll = () => {
    if (extractPollRef.current) {
      window.clearInterval(extractPollRef.current);
      extractPollRef.current = null;
    }
  };

  const abandonExtractPoll = () => {
    extractGenerationRef.current += 1;
    stopExtractPoll();
  };

  const startNewReport = () => {
    setWorkspaceView("report");
    setError(null);
    setResearchContinueError(null);
    setResearchStalled(false);
    abandonExtractPoll();
    reviseAttemptRef.current += 1;
    setRevising(false);
    nextDraftSelectionVersion();
    stopResearchPoll();
    generateGenerationRef.current += 1;
    stopGeneratePoll();
    generateStartedSetIdRef.current = null;
    setGenerateBusy(false);
    setGenerationStatus(null);
    setGenerateProgressOpen(false);
    setGenerateStage("writing_brief");
    setSelectedFiles([]);
    setProposalRows([]);
    setExtractRunId(null);
    setTemplate(null);
    setSelectedTemplateId("");
    setTemplateName("");
    setTemplateLabel("");
    savedPreparationRef.current = "";
    failedPreparationRef.current = "";
    setPreparationSaved(false);
    setFacts("");
    setResultMd(null);
    setOutline(null);
    setView("preview");
    setReviseText("");
    setWorkspaceTab("preparation");
    setPreparationStep("template");
    setLastSavedDraftId(null);
    setLastSavedSnapshot(null);
    setRevisions([]);
    setRevisionsReady(false);
    setCurrentRevisionNumber(null);
    setResearchSet(null);
    setResearchDocs([]);
    setResearchFiles([]);
    setReusedSources([]);
    setLoadedResearchSetId(null);
    setSourcesLoading(false);
    setSourcesError(null);
    setResearchAnswerTexts({});
    setResearchSubmitting(false);
    setResearchContinuePending(false);
    setShowToolbar(false);
    setShowSuccessDialog(false);
    setProgressOpen(false);
    setProgressMetrics({ processed: 0, total: 0 });
    setDisplayProgress({ processed: 0, total: 0 });
    setSelectedDraft(null);
  };

  useEffect(() => {
    if (!lastSavedDraftId) return;
    const row = drafts.find((d) => d.id === lastSavedDraftId);
    if (row?.discarded_at) {
      startNewReport();
    }
  }, [drafts, lastSavedDraftId]);

  useEffect(() => {
    if (template || !selectedTemplateId) return;
    const t = templates.find(x => x.id === selectedTemplateId);
    if (t) {
      setTemplate(t.template_json);
      setTemplateLabel(t.name);
      setProposalRows(toProposalSections(t.template_json?.sections || []));
      if (selectedTemplateId === selectedDraft?.staff_report_template_id) {
        setLastSavedSnapshot((prev) => prev && { ...prev, workspaceState: JSON.stringify({ template: t.template_json, answers: selectedDraft.workspace_state?.answers ?? {}, extract_run_id: selectedDraft.workspace_state?.extract_run_id ?? null }) });
      }
    }
  }, [selectedTemplateId, templates, template]);

  researchMunicipalityIdRef.current = municipality?.id ?? null;

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
      }
      if (generatePollRef.current) {
        window.clearInterval(generatePollRef.current);
      }
      if (extractPollRef.current) {
        window.clearInterval(extractPollRef.current);
      }
      if (progressAnimationRef.current) {
        window.clearInterval(progressAnimationRef.current);
      }
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = Array.from(e.target.files || []);
    const accepted = files.filter(isTemplateFile);
    const skipped = files.length - accepted.length;
    if (accepted.length === 0) {
      setSelectedFiles([]);
      setError("Upload a PDF, Word, text, or Markdown file.");
      e.currentTarget.value = "";
      return;
    }
    if (skipped > 0) {
      setError(`${skipped} file${skipped === 1 ? " was" : "s were"} skipped because they are not documents.`);
    }
    setSelectedFiles(accepted);
  };

  const applyProposal = (proposal: { title?: string; sections: TemplateSection[]; style?: unknown }) => {
    setTemplate({ title: proposal.title || templateName, sections: proposal.sections, style: proposal.style });
    setProposalRows(toProposalSections(proposal.sections || []));
    if (proposal.title) setTemplateLabel(proposal.title);
  };

  const handleExtract = async () => {
    try {
      if (!municipality?.id) { setError("Please select a municipality."); return; }
      if (selectedFiles.length === 0) { setError("Select at least one file first."); return; }
      setError(null);
      setProgressOpen(false);
      setExtractProgressOpen(true);
      setProgressTitle("Extracting template");
      setProgressBody(selectedFiles.length === 1 ? `Reading ${selectedFiles[0].name}…` : `Reading ${selectedFiles.length} files…`);
      setResultMd(null);
      setOutline(null);
      setSelectedTemplateId("");
      setProposalRows([]);
      setTemplate(null);
      const generation = extractGenerationRef.current + 1;
      extractGenerationRef.current = generation;
      const run = await startStaffReportTemplateExtract({ files: selectedFiles, municipalityId: municipality.id });
      if (extractGenerationRef.current !== generation) return;
      setExtractRunId(run.id);
      stopExtractPoll();
      let polls = 0;
      let failures = 0;
      const poll = async () => {
        if (extractGenerationRef.current !== generation) return;
        polls += 1;
        if (polls > EXTRACT_POLL_MAX) {
          stopExtractPoll();
          setExtractProgressOpen(false);
          setError("Template extraction failed.");
          return;
        }
        try {
          const status = await getStaffReportTemplateExtract(run.id);
          if (extractGenerationRef.current !== generation) return;
          failures = 0;
          if (status.status === "parsing") setProgressBody("Parsing files…");
          if (status.status === "extracting") setProgressBody("Extracting sections…");
          if (status.status === "combining") setProgressBody("Combining templates…");
          if (!EXTRACT_TERMINAL.has(status.status)) return;
          stopExtractPoll();
          setExtractProgressOpen(false);
          if (status.status === "failed") {
            setError(status.error_message || "Template extraction failed.");
            return;
          }
          if (status.proposal?.sections?.length) {
            applyProposal(status.proposal);
            const failed = (status.files || []).filter((file) => file.status === "failed");
            if (failed.length > 0) {
              setError(failed.map((file) => `${file.original_filename}: ${file.error_message || "failed"}`).join(" "));
            }
            return;
          }
          setError(status.error_message || "Template extraction failed.");
        } catch (pollErr: unknown) {
          if (extractGenerationRef.current !== generation) return;
          failures += 1;
          if (failures < 5) return;
          stopExtractPoll();
          setExtractProgressOpen(false);
          setError(pollErr instanceof Error ? pollErr.message : "Template extraction failed.");
        }
      };
      void poll();
      extractPollRef.current = window.setInterval(() => { void poll(); }, EXTRACT_POLL_MS) as unknown as number;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Template extraction failed.");
      setExtractProgressOpen(false);
    }
  };

  const stopResearchPoll = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const stopGeneratePoll = () => {
    if (generatePollRef.current) {
      window.clearInterval(generatePollRef.current);
      generatePollRef.current = null;
    }
  };

  const generationStale = (generation: number, municipalityId: number) =>
    generateGenerationRef.current !== generation || researchMunicipalityIdRef.current !== municipalityId;

  const applyGenerateRun = (run: StaffReportRun) => {
    setGenerationStatus(run.status);
    if (run.stage) setGenerateStage(run.stage);
    switch (run.status) {
      case "queued":
      case "running":
        return false;
      case "done":
        stopGeneratePoll();
        setGenerateBusy(false);
        setGenerateProgressOpen(false);
        const markdown = run.generated_report_markdown || "";
        setResultMd(markdown);
        setLastSavedSnapshot((prev) => (prev ? { ...prev, markdown } : prev));
        setDrafts((prev) => prev.map((draft) => draft.id === lastSavedDraftIdRef.current ? { ...draft, stage: "ready_to_review" } : draft));
        setOutline((run.outline || []).map((item) => ({ title: item.title, bullets: item.bullets || [] })));
        setWorkspaceTab("report");
        setError(null);
        return true;
      case "failed":
        stopGeneratePoll();
        generateStartedSetIdRef.current = null;
        setGenerateBusy(false);
        setGenerateProgressOpen(false);
        setError(run.error || "Ordinal couldn't write this report. Try again shortly.");
        return true;
      default: {
        const unknown: never = run.status;
        setError(unknown);
        return true;
      }
    }
  };
  applyGenerateRunRef.current = applyGenerateRun;

  const startGeneratePoll = (runId: number, municipalityId: number) => {
    stopGeneratePoll();
    const scope = municipalityId;
    const generation = generateGenerationRef.current;
    const started = Date.now();
    let failures = 0;
    let stopped = false;
    let seenRun = false;
    const stopFor = (message: string) => {
      stopped = true;
      stopGeneratePoll();
      generateStartedSetIdRef.current = null;
      setGenerateBusy(false);
      setGenerateProgressOpen(false);
      setError(message);
    };
    const stale = () => stopped || generationStale(generation, scope);
    const tick = async () => {
      if (stale()) return;
      if (Date.now() - started >= GENERATE_POLL_MAX_MS) {
        stopFor("This is taking longer than expected.");
        return;
      }
      try {
        const run = await getStaffReportRun(runId);
        if (stale()) return;
        seenRun = true;
        failures = 0;
        if (applyGenerateRunRef.current(run)) stopped = true;
      } catch (pollErr) {
        if (stale()) return;
        console.error("[StaffReports] Generate poll error:", pollErr);
        failures += 1;
        const limit = seenRun ? GENERATE_POLL_FAILURE_LIMIT * 5 : GENERATE_POLL_FAILURE_LIMIT;
        if (failures >= limit) {
          stopFor("This is taking longer than expected.");
        }
      }
    };
    void tick();
    generatePollRef.current = window.setInterval(tick, GENERATE_POLL_MS);
  };

  const beginGenerate = async (set: StaffReportResearchSet) => {
    const municipalityId = municipality?.id;
    if (!municipalityId) return;
    if (generateStartedSetIdRef.current === set.id) return;
    const generation = ++generateGenerationRef.current;
    generateStartedSetIdRef.current = set.id;
    setGenerateBusy(true);
    setError(null);
    setGenerateStage("writing_brief");
    setGenerateProgressOpen(true);
    try {
      const created = await startStaffReportRun({
        municipalityId,
        draftId: await workspaceSaverRef.current(),
        researchSetId: set.id,
        templateId: typeof selectedTemplateId === "number" ? selectedTemplateId : undefined,
        template,
      });
      if (generateGenerationRef.current !== generation) return;
      if (researchMunicipalityIdRef.current !== municipalityId) {
        generateStartedSetIdRef.current = null;
        setGenerateBusy(false);
        setGenerateProgressOpen(false);
        return;
      }
      if (!applyGenerateRun(created)) startGeneratePoll(created.id, municipalityId);
    } catch (err: unknown) {
      if (generateGenerationRef.current !== generation) return;
      generateStartedSetIdRef.current = null;
      setGenerateBusy(false);
      setGenerateProgressOpen(false);
      setError(err instanceof Error ? err.message : "Ordinal couldn't write this report. Try again shortly.");
    }
  };

  useEffect(() => {
    if (previousMunicipalityId.current !== municipality?.id) {
      previousMunicipalityId.current = municipality?.id;
      setDrafts([]);
      startNewReport();
      setWorkspaceView("history");
      return;
    }
  }, [municipality?.id]);

  const applyResearchSet = (set: StaffReportResearchSet) => {
    setResearchSet(set);
    setResearchDocs(set.documents || []);
    const docs = set.documents || [];
    const processed = docs.filter((doc) => ["done", "failed", "skipped"].includes(doc.status)).length;
    setProgressMetrics({ processed, total: docs.length });
    setResearchProgressBody(`Processing ${docs.length} file${docs.length === 1 ? "" : "s"}…`);

    if (isResearchTerminal(set.status)) {
      stopResearchPoll();
      setProgressOpen(false);
      setResearchSubmitting(false);
      const fileError = docs.some((doc) => doc.error_message);
      if (set.status === "failed" && !fileError) {
        setError(set.error_message || "Research parsing failed.");
      } else {
        setError(null);
      }
    }
  };

  const stopResearchPollWithMessage = (message: string) => {
    stopResearchPoll();
    setProgressOpen(false);
    setResearchSubmitting(false);
    setResearchStalled(true);
    setError(message);
  };

  const startResearchPoll = (setId: number, municipalityId: number) => {
    stopResearchPoll();
    const scope = municipalityId;
    const generation = generateGenerationRef.current;
    const started = Date.now();
    let failures = 0;
    let stopped = false;
    const stale = () => stopped || generationStale(generation, scope);
    const tick = async () => {
      if (stale()) return;
      if (Date.now() - started >= RESEARCH_POLL_MAX_MS) {
        stopped = true;
        stopResearchPollWithMessage("This is taking longer than expected.");
        return;
      }
      try {
        const status = await getResearchSet(setId, municipalityId);
        if (stale()) return;
        failures = 0;
        applyResearchSet(status);
      } catch (pollErr) {
        if (stale()) return;
        console.error("[StaffReports] Research poll error:", pollErr);
        failures += 1;
        if (failures >= RESEARCH_POLL_FAILURE_LIMIT) {
          stopped = true;
          stopResearchPollWithMessage("This is taking longer than expected.");
        }
      }
    };
    void tick();
    pollRef.current = window.setInterval(tick, RESEARCH_POLL_MS);
  };

  const submitResearch = async () => {
    if (!municipality?.id) {
      setError("Please select a municipality.");
      return;
    }
    if (!researchSet && (researchFiles.length + reusedSources.length === 0 || researchFiles.length + reusedSources.length > RESEARCH_MAX_FILES)) {
      setError(`Choose between 1 and ${RESEARCH_MAX_FILES} supporting files, including reused files.`);
      return;
    }

    const municipalityId = municipality.id;
    const generation = generateGenerationRef.current;
    setError(null);
    setResearchContinueError(null);
    setResearchStalled(false);
    setResearchSubmitting(true);
    setProgressMetrics({ processed: 0, total: researchFiles.length });
    setDisplayProgress({ processed: 0, total: researchFiles.length });
    setProgressOpen(true);
    setResearchProgressBody(`Uploading ${researchFiles.length} file${researchFiles.length > 1 ? "s" : ""}…`);

    try {
      const draftId = await workspaceSaverRef.current();
      const saved = await getStaffReportDraft(draftId);
      const setId = saved.staff_report_research_set_id;
      if (!setId) throw new Error("Save your supporting files before preparing this report.");
      const created = await prepareResearchSet({ id: setId, municipalityId, description: facts,
        templateHeadings: (template?.sections || []).map((section) => section.heading).filter(Boolean) });
      if (generationStale(generation, municipalityId)) return;
      setResearchSubmitting(false);
      applyResearchSet(created);
      if (!isResearchTerminal(created.status)) {
        startResearchPoll(created.id, municipalityId);
      }
    } catch (err: any) {
      if (generationStale(generation, municipalityId)) return;
      setError(err?.message || "Failed to upload research files");
      setProgressMetrics({ processed: 0, total: 0 });
      setDisplayProgress({ processed: 0, total: 0 });
      setProgressOpen(false);
      setResearchSubmitting(false);
    }
  };

  const continueResearchAnswers = async () => {
    const municipalityId = municipality?.id;
    if (!municipalityId || !researchSet) return;
    const generation = generateGenerationRef.current;
    setResearchContinuePending(true);
    setResearchContinueError(null);
    try {
      const questions = researchSet.questions || [];
      const updated = await continueResearchSet({
        id: researchSet.id,
        municipalityId,
        answers: questions.map((question, index) => ({
          question: question.question,
          answer: researchAnswerTexts[index] ?? "",
        })),
      });
      if (generationStale(generation, municipalityId)) return;
      applyResearchSet(updated);
    } catch (err: any) {
      if (generationStale(generation, municipalityId)) return;
      setResearchContinueError(err?.message || "Could not save answers.");
    } finally {
      if (!generationStale(generation, municipalityId)) {
        setResearchContinuePending(false);
      }
    }
  };

  const handleStep2Continue = async () => {
    if (researchSet?.status === "needs_input") {
      await continueResearchAnswers();
      return;
    }
    if (researchReady && researchSet) {
      await beginGenerate(researchSet);
      return;
    }
    await submitResearch();
  };

  const handlePrintPreview = () => {
    if (!resultMd) return;
    openStaffReportPrintPreview({
      title: templateName || "Staff Report",
      markdown: resultMd,
    });
  };

  const currentProposalTemplate = () => {
    const sections = toTemplateSections(proposalRows);
    return {
      title: templateLabel || template?.title || "Staff report template",
      sections,
      style: template?.style,
    };
  };

  const handleSaveTemplate = async () => {
    try {
      if (!municipality?.id) { setError("Please select a municipality."); return; }
      const proposal = currentProposalTemplate();
      if (proposal.sections.length === 0) { setError("Add at least one section before saving."); return; }
      if (!templateLabel.trim()) { setError("Give this template a name."); return; }
      setLoading(true);
      if (selectedTemplateId) {
        const saved = await updateStaffReportTemplate({
          id: Number(selectedTemplateId),
          name: templateLabel,
          municipalityId: municipality.id,
          templateJson: proposal,
        });
        setTemplates((prev) => prev.map((item) => (item.id === saved.id ? { ...item, ...saved } : item)));
        setTemplate(proposal);
      } else {
        const saved = await createStaffReportTemplate({
          name: templateLabel,
          municipalityId: municipality.id,
          templateJson: proposal,
          extractRunId: extractRunId || undefined,
          file: extractRunId ? undefined : selectedFiles[0],
        });
        setTemplates((prev) => [saved as { id: number; name: string; template_json: typeof proposal }, ...prev]);
        setSelectedTemplateId(saved.id);
        setTemplate(proposal);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardTemplate = async () => {
    if (!selectedTemplateId || !municipality?.id) return;
    try {
      setLoading(true);
      await discardStaffReportTemplate(Number(selectedTemplateId));
      setTemplates((prev) => prev.filter((item) => item.id !== selectedTemplateId));
      setSelectedTemplateId("");
      setTemplate(null);
      setProposalRows([]);
      setTemplateLabel("");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed.");
    } finally {
      setLoading(false);
    }
  };

  const saveStatus = <span role="status" className="text-xs text-muted-foreground">{saving ? "Saving changes…" : generateBusy ? generateStageCopy(generateStage) : researchBusy ? "Reading supporting files…" : !resultMd && preparationSaved && savedPreparationRef.current === preparationSignature && (researchSet || !researchFiles.length && !reusedSources.length) ? "Preparation saved" : resultMd && !isDirty ? "Saved" : "Unsaved changes"}</span>;

  return (
    <Tabs value={workspaceTab} onValueChange={(value) => setWorkspaceTab(value as "preparation" | "report")} className={moduleWorkspaceShellClass} inert={leaving}>
      <ModuleWorkspaceHeader
        title="Report Assistant"
        hideTitle={workspaceView === "report"}
        meta={workspaceView === "report" ? <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="sr-only">{templateName || "New report"}</h1>
          <Input id="report-name" aria-label="Draft name" placeholder="Report name" value={templateName} onChange={(event) => setTemplateName(event.target.value)} className="w-full sm:w-80 border-transparent px-0 text-lg font-semibold shadow-none focus-visible:px-2" />
          <StaffReportStageBadge stage={reportStage} />
          <TabsList aria-label="Report views" className="border">
            <TabsTrigger value="preparation">Preparation</TabsTrigger>
            <TabsTrigger value="report" disabled={!resultMd}>Report</TabsTrigger>
          </TabsList>
        </div> : undefined}
        density="compact"
        actions={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {workspaceView === "report" && <Button variant="ghost" disabled={saving || researchSubmitting || revising || deleting} onClick={async () => {
              if (!await beforeLeaveReportRef.current?.()) return;
              setProgressOpen(false);
              setGenerateProgressOpen(false);
              setExtractProgressOpen(false);
              setWorkspaceView("history");
            }}><ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />All reports</Button>}
            {workspaceView === "report" && <Button disabled={saving || researchSubmitting || deleting || !municipality?.id || !!resultMd && !isDirty} onClick={async () => {
              try { await workspaceSaverRef.current(); if (resultMd) setShowSuccessDialog(true); } catch {}
            }}>{saving ? "Saving…" : resultMd ? "Save draft" : "Save preparation"}</Button>}
            {workspaceView === "history" && <Button variant="outline" disabled={saving || researchSubmitting || revising || deleting || !municipality?.id} onClick={async () => {
              setLeaving(true);
              try {
                if (lastSavedDraftId || templateName || facts || template || researchFiles.length || reusedSources.length) await workspaceSaverRef.current();
                setSaving(true);
                const created = await saveStaffReportDraft({ name: "New report", municipalityId: municipality!.id });
                startNewReport();
                setDrafts((prev) => [created, ...prev.filter((draft) => draft.id !== created.id)]);
                setSelectedDraft({ ...created, markdown: null, municipality_id: municipality!.id });
              } catch (error) { setError(error instanceof Error ? error.message : "Could not start a report."); }
              finally { setSaving(false); setLeaving(false); }
            }}>
              New Report
            </Button>}
            {workspaceView === "report" && lastSavedDraftId && <Button variant="ghost" size="icon" aria-label="Delete draft" disabled={saving || deleting} onClick={async () => {
              if (!window.confirm("Are you sure you want to delete this draft?")) return;
              try {
                setDeleting(true);
                await discardStaffReportDraft(lastSavedDraftId);
                setDrafts((prev) => prev.filter((draft) => draft.id !== lastSavedDraftId));
                startNewReport();
                setWorkspaceView("history");
              } catch (error) { setError(error instanceof Error ? error.message : "Delete failed."); }
              finally { setDeleting(false); }
            }}><Trash2 className="h-4 w-4" /></Button>}
            <MunicipalitySwitcher
              confirmDescription="Save your latest changes before switching municipalities."
            />
          </div>
        }
      />

      <ModuleWorkspaceBody key={`${workspaceView}-${selectedDraft?.id ?? "new"}`} className="!p-0" contentClassName="!space-y-0 min-h-full">
        <main className="min-w-0 space-y-5 p-4 sm:p-6">
          {workspaceView === "history" ? (
            <div className="mx-auto w-full max-w-5xl space-y-4">
              {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
              <StaffReportDraftsList activeDraftId={lastSavedDraftId} />
            </div>
          ) : <>
        <TabsContent value="preparation" className="mt-0 space-y-5">
          <nav aria-label="Preparation steps" className="sticky top-0 z-10 mx-auto w-full max-w-4xl rounded-lg border bg-background p-2 shadow-sm">
            <ol className="grid grid-cols-2 gap-2">
              <li className="min-w-0">
                <Button variant={preparationStep === "template" ? "secondary" : "ghost"} className="h-auto min-h-12 w-full justify-start gap-2 whitespace-normal px-3 py-2 text-left" onClick={() => setPreparationStep("template")} aria-label="Step 1: Report template" aria-controls={templateSectionId} aria-current={preparationStep === "template" ? "step" : undefined}>
                    <span aria-hidden className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${preparationStep === "template" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>1</span>
                    Report template
                </Button>
              </li>
              <li className="min-w-0">
                <Button ref={detailsStepRef} variant={preparationStep === "details" ? "secondary" : "ghost"} className="h-auto min-h-12 w-full justify-start gap-2 whitespace-normal px-3 py-2 text-left" onClick={() => setPreparationStep("details")} aria-label="Step 2: Report details & files" aria-controls={detailsSectionId} aria-current={preparationStep === "details" ? "step" : undefined}>
                    <span aria-hidden className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${preparationStep === "details" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>2</span>
                    Report details &amp; files
                </Button>
              </li>
            </ol>
          </nav>
          {error && researchSet?.status !== "needs_input" && <p role="alert" className="mx-auto w-full max-w-4xl text-sm text-destructive">{error}</p>}
          <div hidden={preparationStep !== "template"} className={moduleWorkspaceNarrowContentClass}>
            <Card ref={preparationStep === "template" ? preparationPanelRef : undefined} id={templateSectionId} role="region" aria-label="Report template" className="scroll-mt-24 p-4 sm:p-5 space-y-4">
              <div className="space-y-4">
                {/* Select Existing Template (shown first) */}
                <div className="space-y-2">
                  <h2 className="text-base font-semibold"><label htmlFor="template-select">Report template</label></h2>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      id="template-select"
                      aria-label="Select Existing Report Template"
                      className="w-full border rounded px-3 py-2 text-sm bg-background"
                      value={selectedTemplateId}
                      onChange={(e) => {
                        const id = e.target.value ? Number(e.target.value) : "";
                        setSelectedTemplateId(id);
                        const selected = templates.find((item) => item.id === id);
                        if (selected) {
                          setTemplate(selected.template_json);
                          setTemplateLabel(selected.name);
                          setProposalRows(toProposalSections(selected.template_json?.sections || []));
                        }
                      }}
                    >
                      <option value="">— Select a template —</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    {selectedTemplateId !== "" && (
                      <Button type="button" variant="outline" onClick={handleDiscardTemplate} disabled={loading}>
                        Delete
                      </Button>
                    )}
                  </div>
                </div>

                <Accordion type="single" collapsible value={showUploadTemplate ? "upload" : ""} onValueChange={(value) => setShowUploadTemplate(value === "upload")}>
                  <AccordionItem value="upload" className="border-0">
                    <AccordionTrigger className="py-2">Upload reference reports</AccordionTrigger>
                    <AccordionContent className="pt-3">
                      <div className="space-y-2">
                        <Input type="file" id={fileInputId} multiple accept=".pdf,.docx,.txt,.md,application/pdf,text/plain,text/markdown" aria-label="Template files" onChange={handleFileChange} />
                        {selectedFiles.length > 0 && (
                          <p className="text-xs text-muted-foreground">{selectedFiles.map((file) => file.name).join(", ")}</p>
                        )}
                        <Button onClick={handleExtract} disabled={selectedFiles.length === 0 || extractProgressOpen} className="w-full sm:w-auto">
                          {extractProgressOpen ? "Extracting..." : "Extract Template"}
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {proposalRows.length > 0 && (
                  <Accordion type="single" collapsible defaultValue={!selectedTemplateId ? "template" : undefined} key={selectedTemplateId || "extracted"}>
                    <AccordionItem value="template" className="rounded-md border">
                    <AccordionTrigger className="px-3 py-3 text-left">Review or edit template</AccordionTrigger>
                    <AccordionContent className="space-y-3 px-3 pb-3">
                    <Input placeholder="Give this template a name" value={templateLabel} onChange={(e) => {
                      const title = e.target.value;
                      setTemplateLabel(title);
                      setTemplate({ ...currentProposalTemplate(), title });
                    }} aria-label="Template name" />
                    <TemplateProposalEditor
                      rows={proposalRows}
                      onChange={(rows) => {
                        setProposalRows(rows);
                        setTemplate((prev) => ({
                          title: templateLabel || prev?.title || "",
                          sections: toTemplateSections(rows),
                          style: prev?.style,
                        }));
                      }}
                      disabled={loading}
                    />
                    <Button variant="secondary" onClick={handleSaveTemplate} disabled={loading || !templateLabel.trim()}>
                      {selectedTemplateId ? "Save changes" : "Save Template"}
                    </Button>
                    </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => { setPreparationStep("details"); detailsStepRef.current?.focus(); }}>
                  Continue to report details<ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </Button>
              </div>
            </Card>
          </div>

          <div hidden={preparationStep !== "details"} className={moduleWorkspaceNarrowContentClass}>
            <Card ref={preparationStep === "details" ? preparationPanelRef : undefined} id={detailsSectionId} role="region" aria-label="Report details & files" className="scroll-mt-24 p-4 sm:p-5 space-y-4">
              <h2 className="text-base font-semibold">Report details &amp; files</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor={factsInputId}>Tell us about the report you want to write</label>
                  <Textarea
                    id={factsInputId}
                    rows={3}
                    placeholder="Optional. Include the request, location, and meeting date."
                    value={facts}
                    disabled={researchInputsLocked}
                    onChange={(e) => setFacts(e.target.value)}
                  />
                </div>

                {municipality?.id && !sourcesLoading && !sourcesError && <StaffReportSources
                  key={`${municipality.id}-${selectedDraft?.id ?? "new"}`}
                  municipalityId={municipality.id} files={researchFiles} onFilesChange={setResearchFiles}
                  reused={reusedSources} onReusedChange={setReusedSources} documents={researchDocs}
                  researchSetId={researchSet?.id ?? loadedResearchSetId ?? undefined} locked={!!researchSet}
                  uploading={researchSubmitting || saving}
                />}
                {!resultMd && (researchSet?.status === "collecting" || researchSet?.status === "failed" || researchStalled) && <Button variant="outline" disabled={saving} onClick={() => {
                  stopResearchPoll();
                  generateGenerationRef.current += 1;
                  setResearchSet(null);
                  setLoadedResearchSetId(null);
                  setResearchDocs([]);
                  if (researchSet?.status === "collecting") {
                    setResearchFiles([]);
                    setReusedSources([]);
                  }
                  setResearchAnswerTexts({});
                  setResearchStalled(false);
                  setError(null);
                }}>{researchSet?.status === "collecting" ? "Replace supporting files" : "Change files and retry"}</Button>}
                {sourcesLoading && <p role="status" className="text-sm text-muted-foreground">Loading supporting files…</p>}
                {sourcesError && <div role="alert" className="text-sm text-destructive">{sourcesError} <Button size="sm" variant="outline" onClick={() => setSourceRetry((value) => value + 1)}>Retry loading files</Button></div>}

                {researchSet?.status === "needs_input" && (researchSet.questions || []).length > 0 && (
                  <ResearchQuestionsPanel
                    questions={researchSet.questions}
                    answerTexts={researchAnswerTexts}
                    pending={researchContinuePending}
                    error={researchContinueError}
                    onAnswerTextChange={(index, value) => {
                      setResearchAnswerTexts((prev) => ({ ...prev, [index]: value }));
                    }}
                    onContinue={() => { void continueResearchAnswers(); }}
                  />
                )}

                {generateBusy && !generateProgressOpen && (
                  <p className="text-sm text-muted-foreground" role="status" aria-live="polite">{generateStageCopy(generateStage)}</p>
                )}
              </div>

              {!resultMd && <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t">
                {saveStatus}
                <Button
                  onClick={() => { void handleStep2Continue(); }}
                  disabled={saving || !canProceedFromStep1 || (researchReady ? generateBusy : !canProceedFromStep2)}
                >
                  {researchReady ? "Write report" : researchBusy ? "Parsing…" : "Prepare report"}
                </Button>
              </div>}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="report" className="mt-0">
        {(resultMd || outline) && (
            <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
              <Card className="p-5 sm:p-8 space-y-4 flex flex-col min-w-0 min-h-[65vh]" ref={draftRef as any}>
                {resultMd && (
                  <div className="space-y-2 flex flex-col min-h-0 flex-1">
                    {error && <p className="text-xs text-destructive" role="alert">{error}</p>}

                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant={view === 'edit' ? 'default' : 'secondary'}
                          onClick={() => {
                            setView('edit');
                            setShowToolbar(false);
                          }}
                        >
                          Edit
                        </Button>
                        <Button variant={view === 'preview' ? 'default' : 'secondary'} onClick={() => setView('preview')}>
                          Preview
                        </Button>
                        <Button
                          variant={showToolbar ? 'default' : 'secondary'}
                          onClick={() => setShowToolbar(!showToolbar)}
                          disabled={view !== 'edit'}
                        >
                          Format
                        </Button>
                        <Button variant="secondary" onClick={async () => { try { await navigator.clipboard.writeText(resultMd || ''); } catch {} }}>
                          <CopyIcon className="mr-2 h-4 w-4" />
                          Copy
                        </Button>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" onClick={handlePrintPreview}>
                            <Printer className="mr-2 h-4 w-4" />
                            Print / Save PDF
                          </Button>
                          {saveStatus}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 min-h-0 pt-5">
                      {view === 'edit' && (
                        <RichTextEditor initialMarkdown={resultMd || ''} onChangeMarkdown={setResultMd} className="h-full" showToolbar={showToolbar} />
                      )}
                      {view === 'preview' && (
                        <div className="prose dark:prose-invert max-w-none">
                          <SafeMarkdown remarkPlugins={[remarkGfm]}>
                            {resultMd}
                          </SafeMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>

              <aside aria-label="Report assistant tools" className="space-y-5 min-w-0">
                  <div className="space-y-2">
                    <div id="staff-report-ai-feedback-heading" className="text-sm font-semibold flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      AI Feedback
                    </div>
                    <Textarea rows={4} aria-labelledby="staff-report-ai-feedback-heading" placeholder="Describe the edit you want... e.g., 'Expand on Subject to include applicant history and cite CEQA exemption.'" value={reviseText} onChange={(e) => setReviseText(e.target.value)} />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        disabled={revising || !resultMd || !reviseText.trim()}
                        onClick={async () => {
                          const attempt = ++reviseAttemptRef.current;
                          const submittedMarkdown = resultMd || "";
                          const persistDraftId = lastSavedDraftId;
                          const selectionVersion = getDraftSelectionVersion();
                          const stillCurrent = () => (
                            reviseAttemptRef.current === attempt
                            && getDraftSelectionVersion() === selectionVersion
                            && (resultMdRef.current || "") === submittedMarkdown
                            && lastSavedDraftIdRef.current === persistDraftId
                          );
                          try {
                            setRevising(true);
                            const resp = await reviseStaffReport({ markdown: submittedMarkdown, instruction: reviseText, template, municipalityId: municipality?.id });
                            if (!stillCurrent()) return;
                            setResultMd(resp.markdown);
                            setView('preview');

                            try {
                              const revision = await persistRevision(reviseText, resp.markdown, persistDraftId);
                              if (!revision || reviseAttemptRef.current !== attempt) return;
                              setRevisions((prev) => [revision, ...prev]);
                              setRevisionsReady(true);
                              setCurrentRevisionNumber(revision.revision_number);
                            } catch (persistError: unknown) {
                              if (reviseAttemptRef.current !== attempt) return;
                              const message = persistError instanceof Error ? persistError.message : "Save failed";
                              setError(message);
                            }
                            setReviseText("");

                            setTimeout(scrollToDraft, 50);
                          } catch (e: any) {
                            if (reviseAttemptRef.current !== attempt) return;
                            setError(e?.message || 'Revision failed');
                          } finally {
                            if (reviseAttemptRef.current === attempt) setRevising(false);
                          }
                        }}
                      >
                        {revising ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Applying...
                          </>
                        ) : (
                          'Apply AI Edit'
                        )}
                      </Button>
                    </div>
                  </div>
                  <Accordion type="multiple">
                  <AccordionItem value="sources" className="border-t border-b-0">
                    <AccordionTrigger className="text-left"><span>Supporting files ({researchDocs.length}){researchDocs.some((document) => document.status === "failed") && <span className="block pt-1 text-xs font-normal text-destructive">Some files couldn't be read</span>}</span></AccordionTrigger>
                    <AccordionContent>
                      {sourcesLoading && <p role="status" className="text-sm text-muted-foreground">Loading supporting files…</p>}
                      {sourcesError && <div role="alert" className="text-sm text-destructive">{sourcesError} <Button size="sm" variant="outline" onClick={() => setSourceRetry((value) => value + 1)}>Retry loading files</Button></div>}
                      {!sourcesLoading && !sourcesError && municipality?.id && <StaffReportSources municipalityId={municipality.id} files={[]} onFilesChange={setResearchFiles} reused={[]} onReusedChange={setReusedSources} documents={researchDocs} researchSetId={researchSet?.id ?? loadedResearchSetId ?? undefined} locked hideHeading />}
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="revisions" className="border-t border-b-0">
                      <AccordionTrigger className="text-left">Revision History ({revisions.length})</AccordionTrigger>
                      <AccordionContent className="space-y-2">
                      {revisions.length === 0 ? (
                        revisionsReady ? <div className="text-sm text-muted-foreground">No revisions yet</div> : null
                      ) : (
                      <div className="ordinal-scrollbar max-h-80 space-y-2 overflow-y-auto pr-2 text-sm">
                        {revisions.map((rev, idx) => {
                          const isCurrentRevision = rev.revision_number === currentRevisionNumber;
                          const prevRevision = revisions[idx + 1];

                          return (
                            <div
                              key={rev.id}
                              className={`rounded p-3 border ${isCurrentRevision ? 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800' : 'bg-muted border-transparent'}`}
                            >
                              <p className="font-medium [overflow-wrap:anywhere]">{rev.instruction}</p>
                              <div className="mt-2 flex items-center justify-between gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {new Date(rev.created_at).toLocaleString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </span>
                                <div className="flex shrink-0 gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setResultMd(rev.markdown);
                                      setCurrentRevisionNumber(rev.revision_number);
                                    }}
                                    className="h-8 w-8"
                                    title="Jump to this revision"
                                    aria-label={`Jump to this revision: ${rev.instruction}`}
                                  >
                                    <Check className="h-4 w-4" aria-hidden />
                                  </Button>
                                  {prevRevision && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setResultMd(prevRevision.markdown);
                                        setCurrentRevisionNumber(prevRevision.revision_number);
                                      }}
                                      className="h-8 w-8"
                                      title="Undo this revision"
                                      aria-label={`Undo this revision: ${rev.instruction}`}
                                    >
                                      <Undo2 className="h-4 w-4" aria-hidden />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                        );
                      })}
                    </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                  </Accordion>
              </aside>
          </div>
        )}
        </TabsContent>
        </>}
        </main>
      </ModuleWorkspaceBody>

      <DismissibleProgressDialog
        open={extractProgressOpen}
        title={progressTitle}
        body={progressBody}
        onDismiss={() => {
          abandonExtractPoll();
          setExtractProgressOpen(false);
        }}
      />
      <DismissibleProgressDialog
        open={generateProgressOpen}
        title="Drafting report"
        body={generateStageCopy(generateStage)}
        onDismiss={() => setGenerateProgressOpen(false)}
      />
      <Dialog open={progressOpen} onOpenChange={setProgressOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Parsing research</DialogTitle>
            <DialogDescription>{researchProgressBody}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4 w-full min-w-0">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            {showDocumentProgress ? (
              <div className="w-full min-w-0 space-y-3">
                <Progress
                  value={progressMetrics.total > 0
                    ? (displayProgress.processed / progressMetrics.total) * 100
                    : 0}
                />
                <p className="text-sm text-muted-foreground text-center">
                  {displayProgress.processed} of {progressMetrics.total || "…"} files processed
                </p>
                <div className="max-h-56 space-y-2 overflow-y-auto overflow-x-hidden">
                  {researchDocs.map((doc) => (
                    <div key={doc.id} className="min-w-0 space-y-0.5">
                      <div className="flex min-w-0 items-center justify-between gap-3 text-xs text-muted-foreground">
                        <span className="min-w-0 flex-1 truncate">{doc.original_filename}</span>
                        <span className="shrink-0">{researchDocumentLabel(doc.status)}</span>
                      </div>
                      {doc.error_message ? (
                        <p className="text-xs text-destructive" role="alert">{doc.error_message}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Draft saved</DialogTitle>
            <DialogDescription>Your draft has been saved.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-6 py-4">
            <CheckCircle2 className="h-16 w-16 text-green-600 animate-pulse" />
            <Button
              variant="secondary"
              onClick={() => {
                setShowSuccessDialog(false);
              }}
              className="w-full"
            >
              Back to Report
            </Button>
            <Button
              onClick={() => {
                startNewReport();
              }}
              className="w-full"
            >
              Create New Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
